/**
 * The finished ink on a page, and everything that can be done to it.
 *
 * Landing a stroke, taking one off, and swapping one for a changed version of itself
 * — with the two indexes that make a three-hour board affordable kept in step:
 * the spatial index the eraser and the select tool hit-test against, and the running
 * box around everything drawn that the scrollbars read on every panned frame.
 *
 * Split out from the ink session so that the session is about the stroke under the
 * hand, and this is about the work already on the board.
 */

import type { Rect, Stroke } from '@opal/shared';

import { BAND_COUNT, paintBand, type BoardMemory } from './ink-paint.js';
import { createQuadtree, type Quadtree } from './quadtree.js';
import { strokeBounds } from './stroke-bounds.js';

/** The board's finished ink, as the operations below need to see it. */
export interface BoardInk {
  readonly finished: Stroke[];
  /**
   * The strokes split into paint bands, lowest first (SRS C4, SRS C8).
   *
   * Highlighter ink under pen ink, and the whole of the teacher-only layer over the
   * whole of the lesson. Kept as lists rather than sorted on every paint, because
   * this is what lets a new stroke be added to the canvas instead of forcing every
   * stroke on a three-hour board to be drawn again.
   */
  readonly bands: Stroke[][];
  readonly byId: Map<string, Stroke>;
  /** The order strokes were drawn in, for putting a culled set back in sequence. */
  readonly order: Map<string, number>;
  /** What the board layer already holds, so a new stroke is added, not redrawn. */
  readonly board: BoardMemory;
  index: Quadtree;
  /** Running union of every stroke's box, so measuring the board is never a scan. */
  drawn: Rect | null;
}

/**
 * The two boxes together.
 *
 * Kept as a running total rather than measured on demand: the scrollbars ask for the
 * drawn area on every panned frame, and a three-hour board is far too much to walk
 * through that often.
 */
export function union(existing: Rect | null, next: Rect): Rect {
  if (!existing) return next;
  const x = Math.min(existing.x, next.x);
  const y = Math.min(existing.y, next.y);
  const right = Math.max(existing.x + existing.width, next.x + next.width);
  const bottom = Math.max(existing.y + existing.height, next.y + next.height);
  return { x, y, width: right - x, height: bottom - y };
}

/** The region the spatial index covers before work spills into its overflow list. */
const INDEXED_REGION: Rect = { x: -100_000, y: -100_000, width: 200_000, height: 200_000 };

/** A set of empty paint bands, one per band. */
export function newBands(): Stroke[][] {
  return Array.from({ length: BAND_COUNT }, () => [] as Stroke[]);
}

/** An empty spatial index over the region the board keeps track of. */
export function newIndex(): Quadtree {
  return createQuadtree(INDEXED_REGION);
}

/** Puts a finished stroke on the board, wherever it was drawn. */
export function land(board: BoardInk, stroke: Stroke): void {
  if (board.byId.has(stroke.id)) return;
  const bounds = strokeBounds(stroke);
  board.finished.push(stroke);
  board.order.set(stroke.id, board.finished.length);
  board.bands[paintBand(stroke)]?.push(stroke);
  board.byId.set(stroke.id, stroke);
  board.index.insert({ id: stroke.id, bounds });
  board.drawn = union(board.drawn, bounds);
}

function forget(strokes: Stroke[], strokeId: string): void {
  const at = strokes.findIndex((stroke) => stroke.id === strokeId);
  if (at >= 0) strokes.splice(at, 1);
}

/**
 * Rebuilds the spatial index and the drawn area from what is left.
 *
 * Both are built up as work lands and neither can have a stroke taken out of it, so
 * removal starts them again. It costs a pass over the board, which is fine: this
 * happens when somebody presses undo, not while they are drawing.
 */
export function rebuildIndex(board: BoardInk): void {
  board.index = newIndex();
  board.drawn = null;
  for (const stroke of board.finished) {
    const bounds = strokeBounds(stroke);
    board.index.insert({ id: stroke.id, bounds });
    board.drawn = union(board.drawn, bounds);
  }
}

/** Takes a stroke off the board. */
export function removeStrokeFrom(board: BoardInk, strokeId: string): boolean {
  if (!board.byId.delete(strokeId)) return false;
  forget(board.finished, strokeId);
  for (const band of board.bands) forget(band, strokeId);
  board.order.delete(strokeId);
  rebuildIndex(board);
  // Paint cannot be lifted off a canvas: the only way to take a line away is to draw
  // the whole board again without it.
  board.board.key = null;
  return true;
}

/** Puts a changed stroke exactly where the one it replaces was sitting. */
function swap(strokes: Stroke[], existing: Stroke, next: Stroke): void {
  const at = strokes.indexOf(existing);
  if (at >= 0) strokes[at] = next;
}

/** Swaps a stroke for a changed version of itself. */
export function replaceStrokeIn(board: BoardInk, stroke: Stroke): boolean {
  const existing = board.byId.get(stroke.id);
  if (!existing) return false;
  swap(board.finished, existing, stroke);
  // The band may change with the stroke — recoloured ink stays put, but work moved to
  // the teacher-only layer has to move bands with it.
  const from = board.bands[paintBand(existing)];
  const to = board.bands[paintBand(stroke)];
  if (from === to) swap(from ?? [], existing, stroke);
  else {
    forget(from ?? [], existing.id);
    to?.push(stroke);
  }
  board.byId.set(stroke.id, stroke);
  rebuildIndex(board);
  // The ink has moved on the layer, and paint cannot be lifted off a canvas: the
  // board has to be drawn again without it.
  board.board.key = null;
  return true;
}

/** The stroke with this id, or null if the board no longer holds it. */
export function findStroke(board: BoardInk, strokeId: string): Stroke | null {
  return board.byId.get(strokeId) ?? null;
}
